import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { JobErrorLogItem } from '../../data/models/job-error-log.models';
import { JobsService } from '../../data/services/jobs.service';

export interface ParsedErrorMessage {
  intro: string;
  requestRaw: string;
  requestObj: any | null;
  responseRaw: string;
  imageUrls: string[];
  referenceImageUrls: string[];
}

@Component({
  selector: 'app-job-error-logs',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './job-error-logs.html',
  styleUrl: './job-error-logs.scss'
})
export class JobErrorLogs implements OnInit, OnDestroy {
  private readonly jobsService = inject(JobsService);

  // Filter properties
  jobId: number | null = null;
  jobImageId: number | null = null;
  processType = '';
  stage = '';
  status = '';
  errorCode = '';
  sourceWorker = '';

  // Pagination
  pageNumber = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages = 0;

  // State
  items: JobErrorLogItem[] = [];
  isLoading = false;
  errorMessage = '';

  // Selected Log for details modal
  selectedLog: JobErrorLogItem | null = null;

  // RxJS subjects for debouncing text changes
  private readonly filterChangedSubject = new Subject<void>();
  private filterChangedSubscription?: Subscription;

  ngOnInit(): void {
    // Set up debouncing for searches
    this.filterChangedSubscription = this.filterChangedSubject.pipe(
      debounceTime(400)
    ).subscribe(() => {
      this.pageNumber = 1;
      this.loadErrorLogs();
    });

    this.loadErrorLogs();
  }

  ngOnDestroy(): void {
    this.filterChangedSubscription?.unsubscribe();
  }

  onFilterChange(): void {
    this.filterChangedSubject.next();
  }

  loadErrorLogs(): void {
    this.isLoading = true;
    this.errorMessage = '';

    const params = {
      jobId: this.jobId,
      jobImageId: this.jobImageId,
      processType: this.processType ? this.processType.trim() : null,
      stage: this.stage ? this.stage.trim() : null,
      status: this.status ? this.status.trim() : null,
      errorCode: this.errorCode ? this.errorCode.trim() : null,
      sourceWorker: this.sourceWorker ? this.sourceWorker.trim() : null,
      pageNumber: this.pageNumber,
      pageSize: this.pageSize
    };

    this.jobsService.getErrorLogs(params).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.items = response.data.items || [];
          this.totalCount = response.data.totalCount || 0;
          this.totalPages = response.data.totalPages || 0;
        } else {
          this.errorMessage = response.message || 'Failed to load error logs.';
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.errorMessage = err.error?.message || 'Unable to load error logs at this time.';
        this.isLoading = false;
      }
    });
  }

  resetFilters(): void {
    this.jobId = null;
    this.jobImageId = null;
    this.processType = '';
    this.stage = '';
    this.status = '';
    this.errorCode = '';
    this.sourceWorker = '';
    this.pageNumber = 1;
    this.loadErrorLogs();
  }

  // Details Modal Handlers
  openDetails(log: JobErrorLogItem): void {
    this.selectedLog = log;
  }

  closeDetails(): void {
    this.selectedLog = null;
  }

  // Pagination Helpers
  get startIndex(): number {
    if (this.totalCount === 0) return 0;
    return (this.pageNumber - 1) * this.pageSize + 1;
  }

  get endIndex(): number {
    return Math.min(this.pageNumber * this.pageSize, this.totalCount);
  }

  goToPrevPage(): void {
    if (this.pageNumber > 1) {
      this.pageNumber--;
      this.loadErrorLogs();
    }
  }

  goToNextPage(): void {
    if (this.pageNumber < this.totalPages) {
      this.pageNumber++;
      this.loadErrorLogs();
    }
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString();
    } catch {
      return dateStr;
    }
  }

  decodeUrl(url: string | null): string {
    if (!url) return '';
    try {
      // Decode unicode/escaped ampersands first.
      // We handle:
      // - \\u0026 (double escaped backslash + u0026)
      // - \u0026 (escaped backslash + u0026)
      // - %5Cu0026 / %5cu0026 (percent-encoded backslash + u0026)
      // - &amp; (HTML entity)
      let decoded = url
        .replace(/\\+u0026/gi, '&')
        .replace(/%5[cC]u0026/gi, '&')
        .replace(/&amp;/gi, '&');

      // Decode safe percent-encoded characters like %3A (colon in dates)
      decoded = decoded.replace(/%3[aA]/g, ':');
      
      return decoded;
    } catch {
      return url;
    }
  }

  isUrl(str: any): boolean {
    if (typeof str !== 'string') return false;
    const decoded = this.decodeUrl(str);
    return decoded.startsWith('http://') || decoded.startsWith('https://');
  }

  getParsedErrorJson(message: string | null): { imageUrl?: string; referenceImageUrl?: string; [key: string]: any } | null {
    if (!message) return null;
    try {
      const trimmed = message.trim();
      // First try to parse the entire string as JSON
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        return JSON.parse(trimmed);
      }
      
      // If that fails, try to find a JSON object in the string (e.g. from { to })
      const startIdx = trimmed.indexOf('{');
      const endIdx = trimmed.lastIndexOf('}');
      if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
        const potentialJson = trimmed.substring(startIdx, endIdx + 1);
        return JSON.parse(potentialJson);
      }
    } catch {
      // Ignored: Not valid JSON
    }
    return null;
  }

  getObjectEntries(obj: any): { key: string; value: any }[] {
    if (!obj) return [];
    return Object.keys(obj).map(key => ({ key, value: obj[key] }));
  }

  getShortErrorMessage(message: string | null): string {
    if (!message) return '-';
    const msgStr = message.trim();
    const requestIndex = msgStr.indexOf('Request:');
    if (requestIndex !== -1) {
      return msgStr.substring(0, requestIndex).trim();
    }
    const responseIndex = msgStr.indexOf('Response:');
    if (responseIndex !== -1) {
      return msgStr.substring(0, responseIndex).trim();
    }
    if (msgStr.startsWith('{') && msgStr.endsWith('}')) {
      try {
        const parsed = JSON.parse(msgStr);
        if (parsed.message) return parsed.message;
        if (parsed.errorMessage) return parsed.errorMessage;
      } catch {
        // Ignored
      }
      return 'JSON Error Payload';
    }
    return msgStr;
  }

  parseErrorMessage(message: string | null): ParsedErrorMessage | null {
    if (!message) return null;

    let intro = '';
    let requestRaw = '';
    let responseRaw = '';
    let requestObj: any = null;
    const imageUrls: string[] = [];
    const referenceImageUrls: string[] = [];

    const msgStr = message.trim();

    // Check if it matches the pattern with "Request:" and "Response:"
    const requestIndex = msgStr.indexOf('Request:');
    const responseIndex = msgStr.indexOf('Response:');

    if (requestIndex !== -1) {
      intro = msgStr.substring(0, requestIndex).trim();
      
      // Clean up separators (like trailing dot or pipe)
      if (intro.endsWith('.')) {
        intro = intro.substring(0, intro.length - 1).trim();
      }
      
      if (responseIndex !== -1 && responseIndex > requestIndex) {
        requestRaw = msgStr.substring(requestIndex + 8, responseIndex).trim();
        // Clean up pipe separator before Response
        if (requestRaw.endsWith('|')) {
          requestRaw = requestRaw.substring(0, requestRaw.length - 1).trim();
        }
        responseRaw = msgStr.substring(responseIndex + 9).trim();
      } else {
        requestRaw = msgStr.substring(requestIndex + 8).trim();
      }
    } else if (responseIndex !== -1) {
      intro = msgStr.substring(0, responseIndex).trim();
      if (intro.endsWith('|')) {
        intro = intro.substring(0, intro.length - 1).trim();
      }
      responseRaw = msgStr.substring(responseIndex + 9).trim();
    } else {
      // Check if the entire string is just a JSON object
      if (msgStr.startsWith('{') && msgStr.endsWith('}')) {
        requestRaw = msgStr;
      } else {
        intro = msgStr;
      }
    }

    // Try to parse requestRaw as JSON
    if (requestRaw) {
      try {
        const cleanedRequest = requestRaw.trim();
        requestObj = JSON.parse(cleanedRequest);
      } catch {
        try {
          const startIdx = requestRaw.indexOf('{');
          const endIdx = requestRaw.lastIndexOf('}');
          if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
            const potentialJson = requestRaw.substring(startIdx, endIdx + 1);
            requestObj = JSON.parse(potentialJson);
          }
        } catch {
          // Ignore parse errors
        }
      }
    }

    // Extract URLs recursively from requestObj
    if (requestObj) {
      this.extractUrlsFromObject(requestObj, imageUrls, referenceImageUrls);
    } else if (requestRaw) {
      this.extractUrlsFromString(requestRaw, imageUrls, referenceImageUrls);
    }

    // Also scan intro and response just in case
    if (responseRaw) {
      this.extractUrlsFromString(responseRaw, imageUrls, referenceImageUrls);
    }
    if (intro) {
      this.extractUrlsFromString(intro, imageUrls, referenceImageUrls);
    }

    // Remove duplicates
    const uniqueImageUrls = Array.from(new Set(imageUrls));
    const uniqueRefUrls = Array.from(new Set(referenceImageUrls));

    // If we have nothing parsed, fall back to null
    if (!intro && !requestRaw && !responseRaw) {
      return null;
    }

    return {
      intro: intro,
      requestRaw: requestRaw,
      requestObj: requestObj,
      responseRaw: responseRaw,
      imageUrls: uniqueImageUrls,
      referenceImageUrls: uniqueRefUrls
    };
  }

  private extractUrlsFromObject(obj: any, imageUrls: string[], referenceImageUrls: string[]) {
    if (!obj || typeof obj !== 'object') return;
    
    if (Array.isArray(obj)) {
      for (const item of obj) {
        this.extractUrlsFromObject(item, imageUrls, referenceImageUrls);
      }
      return;
    }
    
    for (const key of Object.keys(obj)) {
      const value = obj[key];
      if (typeof value === 'string') {
        const isUrlValue = value.startsWith('http://') || value.startsWith('https://') || value.includes('core.windows.net') || value.includes('u0026');
        if (isUrlValue) {
          if (key === 'imageUrl') {
            imageUrls.push(value);
          } else if (key === 'referenceImageUrl') {
            referenceImageUrls.push(value);
          } else if (key.toLowerCase().includes('reference') || key.toLowerCase().includes('ref') || value.toLowerCase().includes('crop')) {
            referenceImageUrls.push(value);
          } else {
            imageUrls.push(value);
          }
        }
      } else if (typeof value === 'object') {
        this.extractUrlsFromObject(value, imageUrls, referenceImageUrls);
      }
    }
  }

  private extractUrlsFromString(str: string, imageUrls: string[], referenceImageUrls: string[]) {
    const urlRegex = /(https?:\/\/[^\s"'}]+)/gi;
    let match;
    while ((match = urlRegex.exec(str)) !== null) {
      const url = match[1];
      if (url.toLowerCase().includes('crop') || url.toLowerCase().includes('reference')) {
        referenceImageUrls.push(url);
      } else {
        imageUrls.push(url);
      }
    }
  }
}
